import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { FormGroup, Validators, FormControl, ValidatorFn, ValidationErrors, AbstractControl } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';

import { AuthService } from '@auth/auth.service';
import { AuthNoticeService } from '@auth/auth-notice/auth-notice.service';
import { SflBaseComponent } from '@shared/components/sfl-base/sfl-base.component';
import { AppConstants } from '@shared/constants/app.constant';
import { ServerErrorMessages } from '@shared/constants/error-messages.constant';
import { finalize } from 'rxjs/operators';

@Component({
  selector: 'kt-change-password',
  templateUrl: './change-password.component.html',
  encapsulation: ViewEncapsulation.None
})
export class ChangePasswordComponent extends SflBaseComponent implements OnInit, OnDestroy {
  changePasswordForm: FormGroup;
  AppConstants = AppConstants;
  cardTitle = 'Change Password';
  cardSubTitle = '';
  constructor(private readonly authService: AuthService, public readonly authNoticeService: AuthNoticeService, private readonly router: Router) {
    super();
  }

  ngOnInit() {
    this.initChangePasswordForm();
  }

  ngOnDestroy(): void {
    this.isSubmitting = false;
    this.authNoticeService.setNotice('');
  }

  initChangePasswordForm() {
    const passwordValidators = [Validators.required, Validators.minLength(AppConstants.requiredMinLength), Validators.maxLength(AppConstants.passwordMaxLength)];
    this.changePasswordForm = new FormGroup(
      {
        current_password: new FormControl('', [Validators.required]),
        password: new FormControl('', passwordValidators),
        password_confirmation: new FormControl('', passwordValidators)
      },
      { validators: [this.passwordValidator] }
    );
  }

  passwordValidator: ValidatorFn = (formGroup: AbstractControl): ValidationErrors | null => {
    const group = formGroup as FormGroup;
    const password = group.get('password')?.value;
    const confirmation = group.get('password_confirmation')?.value;

    if (!password || !confirmation) {
      return null; // don’t validate if one is missing
    }

    return password === confirmation ? null : { passwordNotMatch: true };
  };

  /**
   * Form Submit
   */
  submit() {
    if (!this.changePasswordForm) {
      return;
    }

    const controls = this.changePasswordForm.controls;

    if (this.changePasswordForm.invalid) {
      Object.keys(controls).forEach((controlName) => controls[controlName].markAsTouched());
      return;
    }

    this.isSubmitting = true;
    this.authNoticeService.setNotice('');

    this.subscriptionManager.add(
      this.authService
        .changeMyPassword({
          current_password: controls.current_password.value,
          password: controls.password.value,
          password_confirmation: controls.password_confirmation.value
        })
        .pipe(
          finalize(() => {
            this.isSubmitting = false;
          })
        )
        .subscribe(
          (res) => {
            this.authNoticeService.setNotice(res?.message || res?.data?.message || 'Password updated successfully', 'success');
            setTimeout(() => {
              this.router.navigateByUrl(this.appRoutes.LOGIN);
            }, 3000);
          },
          (error: HttpErrorResponse) => this.showServerError(error)
        )
    );
  }

  private showServerError(error: HttpErrorResponse) {
    const unplaced = ServerErrorMessages.applyToForm(this.changePasswordForm, error);
    const hasFieldErrors = ServerErrorMessages.fieldRefusals(error).length > unplaced.length;
    const message = unplaced.length ? unplaced.join(', ') : hasFieldErrors ? 'Password was not changed. Please fix the highlighted fields.' : null;
    this.authNoticeService.setNotice(message || error?.error?.message || 'Password update failed. Please try again.', 'danger');
  }

  /**
   * Navigate back to login
   */
  goBack() {
    this.router.navigateByUrl(this.appRoutes.LOGIN);
  }
}
